import { RotSprite, imagePix, type Pix } from "./rotsprite";
import { paintWreck } from "./sky";

export interface Anchors {
  weapons: Record<string, { grip?: number[]; fore?: number[]; muzzle?: number[]; eject?: number[] }>;
  bomblet: { tail?: number[] };
  parachute: { cords?: number[] };
  jetpack: { pivot?: number[]; nozzles?: number[][] };
  drone: { pivot?: number[]; mount?: number[]; rotors?: number[][]; eye?: number[]; frame?: number[] };
  gatling: { pivot?: number[]; muzzle?: number[]; frame?: number[] };
}

export interface Sprite {
  img: HTMLImageElement;
  w: number;
  h: number;
  frames: number;
  /** Trimmed frame-0 pixels. Grip coordinates in the file are offset by trimX/trimY. */
  pix: Pix;
  trimX: number;
  trimY: number;
  framePix: Pix[];
}

function trimSheet(src: Pix, frames: number): { framePix: Pix[]; trimX: number; trimY: number } {
  const frameW = Math.floor(src.w / frames);
  let minX = frameW;
  let minY = src.h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < src.h; y++) {
    for (let x = 0; x < frameW * frames; x++) {
      if ((src.data[y * src.w + x] >>> 24) < 128) continue;
      const local = x % frameW;
      if (local < minX) minX = local;
      if (local > maxX) maxX = local;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return { framePix: [src], trimX: 0, trimY: 0 };
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const framePix: Pix[] = [];
  for (let frame = 0; frame < frames; frame++) {
    const data = new Uint32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const color = src.data[(minY + y) * src.w + frame * frameW + minX + x];
        if (color >>> 24 >= 128) data[y * w + x] = color;
      }
    }
    framePix.push({ w, h, data });
  }
  return { framePix, trimX: minX, trimY: minY };
}

const rots = new Map<string, RotSprite>();

/** 8× RotSprite cache. `ax`/`ay` are file pixels; trimming is applied here. */
export function spriteRot(sprite: Sprite, ax: number, ay: number, steps = 128, frame = 0): RotSprite {
  const key = `${sprite.img.src}:${frame}:${ax}:${ay}:${steps}`;
  let rot = rots.get(key);
  if (!rot) {
    const pix = sprite.framePix[frame] ?? sprite.pix;
    rot = new RotSprite(pix, ax - sprite.trimX, ay - sprite.trimY, steps);
    rots.set(key, rot);
  }
  return rot;
}

export async function loadArt(): Promise<{ sprites: Record<string, Sprite>; anchors: Anchors }> {
  const anchors = (await fetch("/assets/sprites/anchors.json").then((r) => r.json())) as Anchors;
  const names = ["pistol", "smg", "shotgun", "launcher", "railgun", "flamethrower", "overkill", "remote", "tesla", "meteor", "grenade", "rocket", "mirv", "bomblet", "parachute", "jetpack", "drone", "gatling"];
  const sprites: Record<string, Sprite> = {};
  await Promise.all(names.map(async (name) => {
    const img = new Image();
    img.src = `/assets/sprites/${name}.png`;
    await img.decode();
    const frames = name === "drone" || name === "gatling" ? 4 : 1;
    const sheet = imagePix(img, img.width, img.height);
    const trimmed = trimSheet(sheet, frames);
    sprites[name] = { img, w: img.width / frames, h: img.height, frames, pix: trimmed.framePix[0], trimX: trimmed.trimX, trimY: trimmed.trimY, framePix: trimmed.framePix };
  }));
  return { sprites, anchors };
}

export interface BgLayer {
  img: HTMLImageElement;
  w: number;
  h: number;
  speed: number;
}
export interface Backdrop {
  sky: string;
  ground: string;
  layers: BgLayer[];
  procedural?: boolean;
}

export const WRECK_BACKDROP: Backdrop = { sky: "#070a17", ground: "#0a0910", layers: [], procedural: true };

export async function loadBackdrops(defs: Array<{ id: string; layers: Array<[string, number]>; procedural?: boolean }>): Promise<Record<string, Backdrop>> {
  const out: Record<string, Backdrop> = {};
  for (const def of defs) {
    if (def.procedural || def.layers.length === 0) {
      out[def.id] = { ...WRECK_BACKDROP };
      continue;
    }
    const layers: BgLayer[] = [];
    for (const [path, speed] of def.layers) {
      const img = new Image();
      img.src = `/assets/bg/${path}.png`;
      await img.decode();
      layers.push({ img, w: img.width, h: img.height, speed });
    }
    const c = document.createElement("canvas");
    c.width = layers[0].w;
    c.height = layers[0].h;
    const g = c.getContext("2d", { willReadFrequently: true })!;
    for (const layer of layers) g.drawImage(layer.img, 0, 0);
    const sky = g.getImageData(0, 0, 1, 1).data;
    const ground = g.getImageData(0, c.height - 1, 1, 1).data;
    out[def.id] = {
      sky: `rgb(${sky[0]},${sky[1]},${sky[2]})`,
      ground: `rgb(${ground[0]},${ground[1]},${ground[2]})`,
      layers,
    };
  }
  return out;
}

export function drawBackdrop(ctx: CanvasRenderingContext2D, bg: Backdrop | null, w: number, h: number, camX: number, scale: number, altitude = 0, now = 0, live = false) {
  if (bg?.procedural) {
    paintWreck(ctx, w, h, camX, scale, altitude, now, live);
    return;
  }
  if (!bg) {
    ctx.fillStyle = "#1b1030";
    ctx.fillRect(0, 0, w, h);
    return;
  }
  ctx.fillStyle = bg.sky;
  ctx.fillRect(0, 0, w, h);
  const horizon = Math.round((h * 0.84) / scale) * scale;
  ctx.fillStyle = bg.ground;
  ctx.fillRect(0, horizon - scale, w, h - horizon + scale);
  for (const layer of bg.layers) {
    const lw = layer.w * scale;
    const lh = layer.h * scale;
    const x0 = -Math.round(((camX * layer.speed * scale / 2) % lw + lw) % lw);
    const y = horizon - lh + Math.round(altitude * lh * 0.3 * (0.3 + layer.speed));
    const yy = Math.round(y / scale) * scale;
    for (let x = x0 - lw; x < w; x += lw) ctx.drawImage(layer.img, x, yy, lw, lh);
  }
}
